import React, { useEffect, useRef, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { fg } from '@confluence/feature-gating';
import { NavigationLockout } from '@confluence/navigation-lockout';
import { useCheckIsLocaleEnglish } from '@confluence/onboarding-helpers/entry-points/hooks/useCheckIsLocaleEnglish.ts';
import { usePagePdfExportPrintShortcut } from '@confluence/page-pdf-export/entry-points/usePagePdfExportPrintShortcut.ts';

import { getPageId, isInsideIframe, isPageViewRoute, getLiveAtlToken } from './printHookUtils.ts';

const i18n = defineMessages({
	lockoutMessage: {
		id: 'pdf-print-utils.navigation-lockout-message',
		description: 'Message warning user about unsaved changes when printing',
		defaultMessage: 'You have unsaved changes. Please save before printing.',
	},
});

function tryGetAtlToken(): string | null {
	try {
		return getLiveAtlToken();
	} catch {
		return null;
	}
}

/**
 * Waits for the atlToken to become available.
 *
 * Why:
 * - atlToken is set during app bootstrap and may not exist on first access.
 * - We poll asynchronously to avoid blocking the UI and to handle race conditions.
 * - We stop after a finite number of attempts to avoid hanging forever.
 *
 * Behavior:
 * - Checks immediately; if token exists, returns it.
 * - Otherwise waits `interval` ms and tries again, up to `maxTries` times.
 * - Throws an Error if the token is still unavailable after the deadline.
 *
 * Defaults (~3s total): maxTries=12, interval=250.
 */
export async function waitForAtlToken(maxTries = 12, interval = 250): Promise<string> {
	let tries = 0;
	for (;;) {
		const t = tryGetAtlToken();
		if (t) return t;
		tries += 1;
		if (tries >= maxTries) throw new Error('atlToken not available');
		await new Promise((r) => setTimeout(r, interval));
	}
}

type UsePrintInterceptOptions = {
	enabled: boolean;
	shouldBlockPrint?: () => boolean;
	onPrintBlocked?: () => void;
};

export function usePrintIntercept({
	enabled,
	shouldBlockPrint,
	onPrintBlocked,
}: UsePrintInterceptOptions): React.JSX.Element | null {
	const attachedRef = useRef(false);
	const shouldBlockPrintRef = useRef(shouldBlockPrint);
	const [showLockout, setShowLockout] = useState(false);
	const intl = useIntl();
	const onPrintShortcut = usePagePdfExportPrintShortcut();
	// The new modal export flow is English-only (matching the overflow-menu gate). Read via a ref so
	// the keydown handler sees the current value without re-attaching listeners.
	const isEnglishLocale = useCheckIsLocaleEnglish();
	const isEnglishLocaleRef = useRef(isEnglishLocale);
	// The keydown listener is attached once and long-lived, so read analytics through a ref that is
	// kept current rather than re-attaching the listener when the hook re-renders.
	const { createEvent } = useConfluenceAnalyticsEvents();
	const createEventRef = useRef(createEvent);

	useEffect(() => {
		shouldBlockPrintRef.current = shouldBlockPrint;
	}, [shouldBlockPrint]);

	useEffect(() => {
		isEnglishLocaleRef.current = isEnglishLocale;
	}, [isEnglishLocale]);

	useEffect(() => {
		createEventRef.current = createEvent;
	}, [createEvent]);

	useEffect(() => {
		if (!enabled) return;
		if (attachedRef.current) return;
		attachedRef.current = true;
		if (isInsideIframe()) return;

		const handler = async (e: KeyboardEvent) => {
			const key = typeof e.key === 'string' ? e.key : '';
			const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
			const isPrintCombo = isMac
				? e.metaKey && (key === 'p' || key === 'P') // mac: only cmd+P
				: e.ctrlKey && (key === 'p' || key === 'P'); // windows/linux: only ctrl+P

			if (!isPrintCombo) return;

			const shouldBlock = shouldBlockPrintRef.current ? shouldBlockPrintRef.current() : false;

			if (shouldBlock) {
				e.preventDefault();
				e.stopImmediatePropagation();
				e.stopPropagation();
				setShowLockout(true);
				onPrintBlocked?.();
				createEventRef
					.current({
						type: 'sendUIEvent',
						data: {
							source: 'pagePdfExport',
							action: 'pressed',
							actionSubject: 'keyboardShortcut',
							actionSubjectId: 'printShortcutBlocked',
							attributes: { exportSource: 'print-shortcut' },
						},
					})
					.fire();
				return;
			}

			if (!isPageViewRoute(window.location.pathname)) return;

			const pageId = getPageId(window.location.pathname);
			if (!pageId) return;

			// Funnel entry for the shortcut flow (the print-shortcut equivalent of the overflow menu's
			// click), fired before the experiment gate so both arms are counted.
			createEventRef
				.current({
					type: 'sendUIEvent',
					data: {
						source: 'pagePdfExport',
						action: 'pressed',
						actionSubject: 'keyboardShortcut',
						actionSubjectId: 'printShortcut',
						attributes: { exportSource: 'print-shortcut', contentId: pageId },
					},
				})
				.fire();

			let atlToken = tryGetAtlToken();
			if (!atlToken) {
				try {
					atlToken = await waitForAtlToken();
				} catch {
					return;
				}
			}

			if (!atlToken || !pageId) return;

			e.preventDefault();
			e.stopImmediatePropagation();
			e.stopPropagation();

			// Gate the new flow on English locale before checking the kill switch, so non-English users
			// stay on the existing hidden-action redirect.
			if (isEnglishLocaleRef.current && fg('cc_mohiti_pdf_export_kill_switch_gate')) {
				try {
					if (await onPrintShortcut(pageId)) return;
				} catch {
					// Fall back to the existing redirect if the experiment callback fails. Report it so a
					// broken experiment flow doesn't silently read as ordinary legacy usage.
					createEventRef
						.current({
							type: 'sendTrackEvent',
							data: {
								source: 'pagePdfExport',
								action: 'failed',
								actionSubject: 'printShortcutExport',
								attributes: { exportSource: 'print-shortcut', contentId: pageId },
							},
						})
						.fire();
				}
			}

			const params = new URLSearchParams({
				pageId,
				atl_token: atlToken,
				showPdfBanner: 'true',
			});
			const url = `${window.location.origin}/wiki/spaces/flyingpdf/pdfpageexport.action?${params.toString()}`;

			window.location.assign(url);
		};

		const opts: AddEventListenerOptions = { capture: true, passive: false };
		window.addEventListener('keydown', handler, opts);
		document.addEventListener('keydown', handler, opts);

		return () => {
			window.removeEventListener('keydown', handler, opts as any);
			document.removeEventListener('keydown', handler, opts as any);
			attachedRef.current = false;
		};
	}, [enabled, onPrintBlocked, onPrintShortcut]);

	const lockoutComponent = showLockout ? (
		<NavigationLockout message={intl.formatMessage(i18n.lockoutMessage)} />
	) : null;

	return lockoutComponent;
}
